import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  api,
} from "@/lib/api";

import {
  useAuth,
} from "@/context/AuthContext";

import {
  PageHeader,
} from "@/components/PageHeader";

import {
  Card,
  Select,
  Empty,
} from "@/components/ui-kit";

import {
  Users,
  IndianRupee,
  Landmark,
  ReceiptText,
  WalletCards,
  BadgeIndianRupee,
  CircleDollarSign,
  Building2,
  ChevronRight,
  AlertTriangle,
  Activity,
  TrendingUp,
  Workflow,
} from "lucide-react";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis,
  Treemap,
} from "recharts";


const COLORS = [
  "#2563eb",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#ef4444",
  "#64748b",
];


const STATUS_COLORS = {
  locked: "#059669",
  finalized: "#10b981",
  calculated: "#2563eb",
  imported: "#8b5cf6",
  draft: "#f59e0b",
  not_started: "#e2e8f0",
};


const compactInr = (value) => {
  const n =
    Number(value || 0);

  if (
    Math.abs(n) >=
    10000000
  ) {
    return (
      `₹${(
        n / 10000000
      ).toFixed(2)}Cr`
    );
  }

  if (
    Math.abs(n) >=
    100000
  ) {
    return (
      `₹${(
        n / 100000
      ).toFixed(2)}L`
    );
  }

  if (
    Math.abs(n) >=
    1000
  ) {
    return (
      `₹${(
        n / 1000
      ).toFixed(1)}K`
    );
  }

  return (
    `₹${n.toLocaleString(
      "en-IN"
    )}`
  );
};


const fullInr = (value) =>
  "₹" +
  Number(
    value || 0
  ).toLocaleString(
    "en-IN",
    {
      maximumFractionDigits: 2,
    }
  );


const axisMoney = (v) => {
  const n =
    Number(v || 0);

  if (n >= 10000000) {
    return `${(
      n / 10000000
    ).toFixed(0)}Cr`;
  }

  if (n >= 100000) {
    return `${(
      n / 100000
    ).toFixed(0)}L`;
  }

  if (n >= 1000) {
    return `${(
      n / 1000
    ).toFixed(0)}K`;
  }

  return n;
};


const KPI_DEFS = [
  {
    key: "active_employees",
    label: "Active Employees",
    icon: Users,
    style: "nk-analytic-blue",
    money: false,
  },
  {
    key: "gross_wages",
    label: "Gross Wages",
    icon: BadgeIndianRupee,
    style: "nk-analytic-amber",
    money: true,
  },
  {
    key: "employee_deductions",
    label: "Employee Deductions",
    icon: WalletCards,
    style: "nk-analytic-rose",
    money: true,
  },
  {
    key: "net_salary",
    label: "Net Salary",
    icon: IndianRupee,
    style: "nk-analytic-green",
    money: true,
  },
  {
    key: "total_statutory",
    label: "Employer Contributions",
    icon: Landmark,
    style: "nk-analytic-violet",
    money: true,
  },
  {
    key: "service_charge",
    label: "Service Charge",
    icon: CircleDollarSign,
    style: "nk-analytic-cyan",
    money: true,
  },
  {
    key: "gst",
    label: "GST",
    icon: ReceiptText,
    style: "nk-analytic-orange",
    money: true,
  },
  {
    key: "invoice_amount",
    label: "Invoice Amount",
    icon: ReceiptText,
    style: "nk-analytic-pink",
    money: true,
  },
  {
    key: "amount_received",
    label: "Received",
    icon: CircleDollarSign,
    style: "nk-analytic-green",
    money: true,
  },
  {
    key: "outstanding",
    label: "Outstanding",
    icon: WalletCards,
    style: "nk-analytic-rose",
    money: true,
  },
];


function AnalyticsCard({
  title,
  subtitle,
  children,
  className = "",
  action,
  onClick,
}) {
  const clickable =
    typeof onClick === "function";

  const activate = (e) => {
    if (!clickable) {
      return;
    }

    const tag =
      e.target?.tagName
        ?.toLowerCase();

    if (
      [
        "button",
        "a",
        "input",
        "select",
      ].includes(tag)
    ) {
      return;
    }

    onClick();
  };

  return (
    <Card
      role={
        clickable
          ? "button"
          : undefined
      }
      tabIndex={
        clickable
          ? 0
          : undefined
      }
      onClick={
        clickable
          ? activate
          : undefined
      }
      onKeyDown={
        clickable
          ? (e) => {
              if (
                e.key === "Enter" ||
                e.key === " "
              ) {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={
        `nk-intel-card ${
          clickable
            ? "nk-intel-card-clickable"
            : ""
        } ${className}`
      }
    >

      <div className="nk-panel-header">

        <div>
          <div className="nk-panel-title">
            {title}
          </div>

          {subtitle && (
            <div className="nk-panel-subtitle">
              {subtitle}
            </div>
          )}
        </div>


        <div className="flex items-center gap-2">

          {action}

          {clickable && (
            <span className="nk-intel-open">
              Open
              <ChevronRight size={13} />
            </span>
          )}

        </div>

      </div>

      {children}

    </Card>
  );
}


function PayrollLifecycle({
  payroll,
  billing,
}) {
  return (
    <div className="nk-lifecycle">

      <div className="nk-lifecycle-group">

        <div className="nk-lifecycle-heading">
          Employee Payroll
        </div>

        <div className="nk-lifecycle-row">

          {payroll.map(
            (item, index) => (
              <React.Fragment
                key={
                  item.name
                }
              >

                <div
                  className={
                    `nk-lifecycle-step nk-life-${item.kind || "default"}`
                  }
                >
                  <span>
                    {item.name}
                  </span>

                  <strong>
                    {compactInr(
                      item.value
                    )}
                  </strong>
                </div>

                {index <
                  payroll.length -
                    1 && (
                  <div className="nk-lifecycle-arrow">
                    →
                  </div>
                )}

              </React.Fragment>
            )
          )}

        </div>

      </div>


      <div className="nk-life-divider" />


      <div className="nk-lifecycle-group">

        <div className="nk-lifecycle-heading">
          Client Billing
        </div>

        <div className="nk-lifecycle-row nk-lifecycle-billing">

          {billing.map(
            (item, index) => (
              <React.Fragment
                key={
                  item.name
                }
              >

                <div className="nk-lifecycle-step nk-life-billing">
                  <span>
                    {item.name}
                  </span>

                  <strong>
                    {compactInr(
                      item.value
                    )}
                  </strong>
                </div>

                {index <
                  billing.length -
                    1 && (
                  <div className="nk-lifecycle-arrow">
                    →
                  </div>
                )}

              </React.Fragment>
            )
          )}

        </div>

      </div>

    </div>
  );
}


function Heatmap({
  rows,
  months,
}) {
  if (
    !rows?.length ||
    !months?.length
  ) {
    return (
      <div className="nk-chart-empty">
        No payroll status history
      </div>
    );
  }

  return (
    <div className="nk-heatmap-wrap">

      <div
        className="nk-heatmap-grid"
        style={{
          gridTemplateColumns:
            `115px repeat(${months.length}, minmax(58px,1fr))`,
        }}
      >

        <div className="nk-heatmap-head">
          Client
        </div>

        {months.map(
          (m) => (
            <div
              key={m}
              className="nk-heatmap-head text-center"
            >
              {m.slice(5)}
            </div>
          )
        )}


        {rows.map(
          (row) => (
            <React.Fragment
              key={
                row.client_id
              }
            >

              <div
                className="nk-heatmap-client"
                title={
                  row.full_name
                }
              >
                {row.name}
              </div>

              {row.months.map(
                (cell) => (
                  <div
                    key={
                      `${row.client_id}-${cell.month}`
                    }
                    className="nk-heatmap-cell"
                    style={{
                      background:
                        STATUS_COLORS[
                          cell.status
                        ] ||
                        "#e2e8f0",
                    }}
                    title={
                      `${row.full_name} · ${cell.month} · ${cell.status.replace(
                        /_/g,
                        " "
                      )}`
                    }
                  />
                )
              )}

            </React.Fragment>
          )
        )}

      </div>


      <div className="nk-heatmap-legend">

        {[
          "not_started",
          "draft",
          "imported",
          "calculated",
          "finalized",
          "locked",
        ].map(
          (status) => (
            <span
              key={status}
            >
              <i
                style={{
                  background:
                    STATUS_COLORS[
                      status
                    ],
                }}
              />

              {status
                .replace(
                  /_/g,
                  " "
                )}
            </span>
          )
        )}

      </div>

    </div>
  );
}


function Funnel({
  rows,
}) {
  if (!rows?.length) {
    return (
      <div className="nk-chart-empty">
        No invoice activity
      </div>
    );
  }

  const max =
    Math.max(
      ...rows.map(
        (r) =>
          Number(
            r.value || 0
          )
      ),
      1
    );

  return (
    <div className="nk-funnel">

      {rows.map(
        (
          row,
          index
        ) => {

          const width =
            48 +
            (
              Number(
                row.value || 0
              ) /
              max
            ) *
              52;

          return (
            <div
              key={
                row.status
              }
              className="nk-funnel-row"
            >

              <div className="nk-funnel-label">
                {row.name}
              </div>

              <div className="nk-funnel-track">

                <div
                  className="nk-funnel-bar"
                  style={{
                    width:
                      `${width}%`,
                    background:
                      COLORS[
                        index %
                        COLORS.length
                      ],
                  }}
                >
                  <strong>
                    {row.value}
                  </strong>
                </div>

              </div>

            </div>
          );
        }
      )}

    </div>
  );
}


export default function Analytics() {
  const {
    selectedClient,
  } = useAuth();

  const navigate =
    useNavigate();

  const [
    searchParams,
  ] = useSearchParams();

  const initialMonth =
    searchParams.get(
      "month"
    ) || "";

  const [
    month,
    setMonth,
  ] = useState(
    initialMonth
  );

  const [
    data,
    setData,
  ] = useState(null);


  useEffect(() => {

    api
      .get(
        "/analytics",
        {
          params: {
            client_id:
              selectedClient,

            month:
              month ||
              undefined,
          },
        }
      )
      .then((r) =>
        setData(
          r.data
        )
      )
      .catch(() => {});

  }, [
    selectedClient,
    month,
  ]);


  const cards =
    data?.cards || {};

  const charts =
    data?.charts || {};

  const meta =
    data?.meta || {};


  const months =
    meta.available_months ||
    [];


  const matrix =
    useMemo(
      () =>
        [
          ...(
            charts.client_matrix ||
            []
          ),
        ],
      [
        charts.client_matrix,
      ]
    );


  const completion =
    charts.completion ||
    {
      processed: 0,
      total: 0,
      percentage: 0,
      pending: 0,
    };


  const clientQuery =
    selectedClient &&
    selectedClient !== "all"
      ? `&client_id=${encodeURIComponent(
          selectedClient
        )}`
      : "";


  const monthQuery =
    month
      ? `month=${encodeURIComponent(
          month
        )}`
      : "";


  const goPayroll = () =>
    navigate(
      `/payroll?${
        monthQuery ||
        "status=processed"
      }${
        monthQuery
          ? "&status=processed"
          : ""
      }${clientQuery}`
    );


  const goInvoices = (
    status = ""
  ) => {
    const parts = [];

    if (month) {
      parts.push(
        `month=${encodeURIComponent(
          month
        )}`
      );
    }

    if (
      selectedClient &&
      selectedClient !== "all"
    ) {
      parts.push(
        `client_id=${encodeURIComponent(
          selectedClient
        )}`
      );
    }

    if (status) {
      parts.push(
        `status=${encodeURIComponent(
          status
        )}`
      );
    }

    navigate(
      `/invoices${
        parts.length
          ? `?${parts.join("&")}`
          : ""
      }`
    );
  };


  const goEmployees = (
    extra = ""
  ) => {
    const parts = [];

    if (
      selectedClient &&
      selectedClient !== "all"
    ) {
      parts.push(
        `client_id=${encodeURIComponent(
          selectedClient
        )}`
      );
    }

    if (extra) {
      parts.push(extra);
    }

    navigate(
      `/employees${
        parts.length
          ? `?${parts.join("&")}`
          : ""
      }`
    );
  };


  const kpiNavigate =
    (key) => {

      const cp =
        selectedClient &&
        selectedClient !== "all"
          ? `&client_id=${encodeURIComponent(
              selectedClient
            )}`
          : "";

      const mp =
        month
          ? `month=${encodeURIComponent(
              month
            )}`
          : "";

      switch (key) {

        case "active_employees":
          navigate(
            `/employees${
              selectedClient &&
              selectedClient !== "all"
                ? `?client_id=${encodeURIComponent(
                    selectedClient
                  )}`
                : ""
            }`
          );
          break;

        case "gross_wages":
        case "employee_deductions":
        case "net_salary":
          navigate(
            `/payroll?${
              mp ||
              "status=processed"
            }${
              mp
                ? "&status=processed"
                : ""
            }${cp}`
          );
          break;

        case "total_statutory":
          navigate(
            `/reports?report=employer_contribution${
              month
                ? `&month=${encodeURIComponent(
                    month
                  )}`
                : ""
            }${cp}`
          );
          break;

        case "service_charge":
          navigate(
            `/reports?report=service_charge_statement${
              month
                ? `&month=${encodeURIComponent(
                    month
                  )}`
                : ""
            }${cp}`
          );
          break;

        case "amount_received":
          navigate(
            `/invoices?status=paid${
              month
                ? `&month=${encodeURIComponent(
                    month
                  )}`
                : ""
            }${cp}`
          );
          break;

        case "outstanding":
          navigate(
            `/invoices?status=pending${
              month
                ? `&month=${encodeURIComponent(
                    month
                  )}`
                : ""
            }${cp}`
          );
          break;

        default:
          navigate(
            `/invoices?${
              mp
            }${cp}`
          );
      }
    };


  return (
    <div className="nk-payroll-intelligence">

      <PageHeader
        title="Payroll Intelligence"
        subtitle="Financial, workforce, compliance and billing analytics"
        actions={
          <Select
            value={month}
            onChange={(e) =>
              setMonth(
                e.target.value
              )
            }
            className="w-44"
          >

            <option value="">
              All Months
            </option>

            {months
              .slice()
              .reverse()
              .map(
                (m) => (
                  <option
                    key={m}
                    value={m}
                  >
                    {m}
                  </option>
                )
              )}

          </Select>
        }
      />


      {/* ====================================================
          KPI STRIP
      ===================================================== */}

      <div className="nk-intel-kpis">

        {KPI_DEFS.map(
          ({
            key,
            label,
            icon: Icon,
            style,
            money,
          }) => (
            <button
              type="button"
              key={key}
              onClick={() =>
                kpiNavigate(
                  key
                )
              }
              className={
                `nk-intel-kpi ${style}`
              }
            >

              <span className="nk-analytics-kpi-icon">
                <Icon size={17} />
              </span>

              <span className="min-w-0 flex-1 text-left">

                <span className="nk-analytics-kpi-label block">
                  {label}
                </span>

                <span className="nk-analytics-kpi-value block">
                  {money
                    ? compactInr(
                        cards[key]
                      )
                    : (
                        cards[key] ??
                        "—"
                      )}
                </span>

              </span>

              <ChevronRight
                size={14}
                className="nk-analytics-card-arrow"
              />

            </button>
          )
        )}

      </div>


      {/* ====================================================
          FINANCIAL LIFECYCLE + COMPLETION + EXCEPTIONS
      ===================================================== */}

      <div className="nk-intel-grid nk-intel-grid-top">

        <AnalyticsCard
          title="Payroll → Billing Lifecycle"
          subtitle="How employee payroll becomes client billing"
          className="nk-intel-span-2"
          onClick={goPayroll}
          action={
            <Workflow
              size={18}
              className="text-blue-500"
            />
          }
        >

          <PayrollLifecycle
            payroll={
              charts.payroll_bridge ||
              []
            }
            billing={
              charts.billing_bridge ||
              []
            }
          />

        </AnalyticsCard>


        <div className="nk-intel-side-stack">

          <AnalyticsCard
            title="Payroll Completion"
            onClick={goPayroll}
            subtitle={
              completion.month ||
              meta.focus_month ||
              "Current period"
            }
          >

            <div className="nk-completion-wrap">

              <div
                className="nk-completion-ring"
                style={{
                  "--progress":
                    `${Math.min(
                      100,
                      Number(
                        completion.percentage ||
                        0
                      )
                    ) * 3.6}deg`,
                }}
              >

                <div className="nk-completion-inner">

                  <strong>
                    {completion.percentage ||
                      0}%
                  </strong>

                  <span>
                    Completed
                  </span>

                </div>

              </div>


              <div className="nk-completion-meta">

                <span>
                  <b>
                    {completion.processed ||
                      0}
                  </b>
                  Processed
                </span>

                <span>
                  <b>
                    {completion.pending ||
                      0}
                  </b>
                  Pending
                </span>

              </div>

            </div>

          </AnalyticsCard>


          <AnalyticsCard
            title="Exceptions"
            subtitle="Items requiring attention"
          >

            <div className="nk-exception-grid">

              {(charts.exceptions || [])
                .map(
                  (item) => (
                    <button
                      type="button"
                      key={
                        item.key
                      }
                      onClick={() => {
                        if (
                          item.key === "pending_payroll" ||
                          item.key === "zero_value_runs"
                        ) {
                          goPayroll();
                          return;
                        }

                        if (
                          item.key === "inactive_employees"
                        ) {
                          goEmployees(
                            month
                              ? `inactivity=1&inactivity_month=${encodeURIComponent(
                                  month
                                )}`
                              : "inactivity=1"
                          );
                          return;
                        }

                        if (
                          item.key === "draft_invoices"
                        ) {
                          goInvoices(
                            "draft"
                          );
                          return;
                        }

                        if (
                          item.key === "overdue_invoices"
                        ) {
                          goInvoices(
                            "overdue"
                          );
                        }
                      }}
                      className={
                        `nk-exception-item nk-ex-${item.severity}`
                      }
                    >

                      <AlertTriangle
                        size={14}
                      />

                      <span>
                        {item.label}
                      </span>

                      <strong>
                        {item.value}
                      </strong>

                    </button>
                  )
                )}

            </div>

          </AnalyticsCard>

        </div>

      </div>


      {/* ====================================================
          FINANCIAL MOMENTUM + AGING
      ===================================================== */}

      <div className="nk-intel-grid">

        <AnalyticsCard
          title="Financial Momentum"
          subtitle="Gross wages, net salary, billing and invoice movement"
          className="nk-intel-span-2"
          onClick={goPayroll}
          action={
            <TrendingUp
              size={18}
              className="text-emerald-500"
            />
          }
        >

          {(charts.monthly_financials || [])
            .length ? (
            <div className="nk-intel-chart nk-intel-chart-wide">

              <ResponsiveContainer>

                <AreaChart
                  data={
                    charts.monthly_financials
                  }
                >

                  <defs>

                    <linearGradient
                      id="grossFill"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#2563eb"
                        stopOpacity={0.18}
                      />

                      <stop
                        offset="95%"
                        stopColor="#2563eb"
                        stopOpacity={0}
                      />
                    </linearGradient>

                    <linearGradient
                      id="invoiceFill"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#10b981"
                        stopOpacity={0.16}
                      />

                      <stop
                        offset="95%"
                        stopColor="#10b981"
                        stopOpacity={0}
                      />
                    </linearGradient>

                  </defs>


                  <CartesianGrid
                    vertical={false}
                    stroke="#edf1f7"
                    strokeDasharray="3 3"
                  />

                  <XAxis
                    dataKey="month"
                    fontSize={9}
                    axisLine={false}
                    tickLine={false}
                  />

                  <YAxis
                    width={48}
                    fontSize={9}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={
                      axisMoney
                    }
                  />

                  <Tooltip
                    formatter={(v) =>
                      fullInr(v)
                    }
                  />

                  <Legend
                    wrapperStyle={{
                      fontSize: 10,
                    }}
                  />

                  <Area
                    type="monotone"
                    dataKey="gross_wages"
                    name="Gross Wages"
                    stroke="#2563eb"
                    strokeWidth={2}
                    fill="url(#grossFill)"
                  />

                  <Line
                    type="monotone"
                    dataKey="net_salary"
                    name="Net Salary"
                    stroke="#8b5cf6"
                    strokeWidth={2.3}
                    dot={{ r: 2 }}
                  />

                  <Line
                    type="monotone"
                    dataKey="billing"
                    name="Client Cost"
                    stroke="#f59e0b"
                    strokeWidth={2.3}
                    dot={{ r: 2 }}
                  />

                  <Area
                    type="monotone"
                    dataKey="invoice_amount"
                    name="Invoice Amount"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#invoiceFill)"
                  />

                </AreaChart>

              </ResponsiveContainer>

            </div>
          ) : (
            <Empty
              title="No financial history"
            />
          )}

        </AnalyticsCard>


        <AnalyticsCard
          title="Receivables Aging"
          subtitle="Outstanding invoice exposure"
          onClick={() => goInvoices("pending")}
        >

          <div className="nk-intel-chart">

            <ResponsiveContainer>

              <BarChart
                data={
                  charts.receivables_aging ||
                  []
                }
              >

                <CartesianGrid
                  vertical={false}
                  stroke="#edf1f7"
                />

                <XAxis
                  dataKey="bucket"
                  fontSize={9}
                  axisLine={false}
                  tickLine={false}
                />

                <YAxis
                  width={42}
                  fontSize={8}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={
                    axisMoney
                  }
                />

                <Tooltip
                  formatter={(v) =>
                    fullInr(v)
                  }
                />

                <Bar
                  dataKey="value"
                  fill="#f59e0b"
                  radius={[
                    7,
                    7,
                    2,
                    2,
                  ]}
                />

              </BarChart>

            </ResponsiveContainer>

          </div>

        </AnalyticsCard>

      </div>


      {/* ====================================================
          CLIENT INTELLIGENCE
      ===================================================== */}

      <div className="nk-intel-grid">

        <AnalyticsCard
          title="Client Payroll Concentration"
          subtitle="Share of payroll cost by client"
          onClick={() => navigate("/clients")}
        >

          <div className="nk-intel-chart">

            <ResponsiveContainer>

              <Treemap
                data={
                  charts.client_cost ||
                  []
                }
                dataKey="value"
                nameKey="name"
                stroke="#ffffff"
                fill="#3b82f6"
                aspectRatio={4 / 3}
              >
                <Tooltip
                  formatter={(v) =>
                    fullInr(v)
                  }
                />
              </Treemap>

            </ResponsiveContainer>

          </div>

        </AnalyticsCard>


        <AnalyticsCard
          title="Client Performance Matrix"
          subtitle="Workforce size compared with payroll cost"
          className="nk-intel-span-2"
          onClick={() => navigate("/clients")}
        >

          {matrix.length ? (
            <div className="nk-intel-chart nk-intel-chart-wide">

              <ResponsiveContainer>

                <ScatterChart
                  margin={{
                    top: 10,
                    right: 15,
                    bottom: 10,
                    left: 8,
                  }}
                >

                  <CartesianGrid
                    stroke="#edf1f7"
                    strokeDasharray="3 3"
                  />

                  <XAxis
                    type="number"
                    dataKey="employees"
                    name="Employees"
                    fontSize={9}
                    axisLine={false}
                    tickLine={false}
                  />

                  <YAxis
                    type="number"
                    dataKey="payroll_cost"
                    name="Payroll Cost"
                    width={50}
                    fontSize={9}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={
                      axisMoney
                    }
                  />

                  <ZAxis
                    type="number"
                    dataKey="cost_per_employee"
                    range={[
                      90,
                      500,
                    ]}
                  />

                  <Tooltip
                    cursor={{
                      strokeDasharray:
                        "3 3",
                    }}
                    formatter={(v, name) =>
                      name ===
                      "Payroll Cost"
                        ? fullInr(v)
                        : v
                    }
                  />

                  <Scatter
                    name="Clients"
                    data={matrix}
                    fill="#2563eb"
                  />

                </ScatterChart>

              </ResponsiveContainer>

            </div>
          ) : (
            <Empty
              title="No client data"
            />
          )}

        </AnalyticsCard>

      </div>


      {/* ====================================================
          OPERATIONS
      ===================================================== */}

      <div className="nk-intel-grid">

        <AnalyticsCard
          title="Payroll Processing Heatmap"
          subtitle="Client status across recent payroll months"
          className="nk-intel-span-2"
          onClick={goPayroll}
        >

          <Heatmap
            rows={
              charts.payroll_heatmap ||
              []
            }
            months={
              charts.heatmap_months ||
              []
            }
          />

        </AnalyticsCard>


        <AnalyticsCard
          title="Invoice Collection Funnel"
          subtitle="Current invoice workflow"
          onClick={() => goInvoices()}
        >

          <Funnel
            rows={
              charts.invoice_funnel ||
              []
            }
          />

        </AnalyticsCard>

      </div>


      {/* ====================================================
          WORKFORCE
      ===================================================== */}

      <div className="nk-intel-grid">

        <AnalyticsCard
          title="Salary Distribution"
          subtitle="Employees grouped by net salary band"
          className="nk-intel-span-2"
          onClick={goPayroll}
        >

          <div className="nk-intel-chart">

            <ResponsiveContainer>

              <BarChart
                data={
                  charts.salary_distribution ||
                  []
                }
              >

                <CartesianGrid
                  vertical={false}
                  stroke="#edf1f7"
                />

                <XAxis
                  dataKey="band"
                  fontSize={9}
                  axisLine={false}
                  tickLine={false}
                />

                <YAxis
                  fontSize={9}
                  axisLine={false}
                  tickLine={false}
                />

                <Tooltip />

                <Bar
                  dataKey="value"
                  name="Employees"
                  fill="#8b5cf6"
                  radius={[
                    7,
                    7,
                    2,
                    2,
                  ]}
                />

              </BarChart>

            </ResponsiveContainer>

          </div>

        </AnalyticsCard>


        <AnalyticsCard
          title="Wage Classification"
          subtitle="Central vs State workforce"
          onClick={() => goEmployees()}
        >

          <div className="nk-intel-chart nk-classification-wrap">

            <ResponsiveContainer>

              <PieChart>

                <Pie
                  data={
                    charts.workforce ||
                    []
                  }
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={78}
                  paddingAngle={4}
                >

                  {(charts.workforce || [])
                    .map(
                      (
                        item,
                        index
                      ) => (
                        <Cell
                          key={
                            item.name
                          }
                          fill={
                            COLORS[
                              index
                            ]
                          }
                        />
                      )
                    )}

                </Pie>

                <Tooltip />

                <Legend
                  wrapperStyle={{
                    fontSize: 10,
                  }}
                />

              </PieChart>

            </ResponsiveContainer>

            <Activity
              className="nk-classification-center"
              size={22}
            />

          </div>

        </AnalyticsCard>

      </div>

    </div>
  );
}
